<template>
    <!--
        La gouttière gauche est celle des étapes 2 à 5 (paddingLeft 55). Elle est explicite
        ici parce que la racine reprend celle de StepTicketOptions, qui l'annule volontairement
        (.ticket-options-wrapper) pour coller sa sidebar de variantes au bord — sidebar que
        cette étape n'a pas.
    -->
    <div
        class="ppv2-flex lg:ppv2-flex-row ppv2-flex-col ppv2-w-full ppv2-step-wrapper"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <div class="ppv2-flex ppv2-flex-col ppv2-flex-1">
            <HeaderStep :step="step" :sizes="sizes" />

            <div class="availability-modes ppv2-flex ppv2-flex-col ppv2-mt-6" :style="{ gap: normalizeSize(14, true) }">
                <!--
                    Les trois cartes sont écrites ici plutôt que via Shared/SelectableList : ce
                    composant n'expose aucun slot par option, alors que la maquette impose
                    d'afficher la configuration DANS la carte sélectionnée (les autres restent
                    repliées). Le vocabulaire visuel reste celui de SelectableList.
                -->
                <div
                    v-for="option in modeOptions"
                    :key="option.value"
                    class="availability-card"
                    :class="{ 'availability-card--selected': mode === option.value }"
                >
                    <div class="availability-card-head" @click="selectMode(option.value)">
                        <span class="availability-radio" :class="{ 'availability-radio--on': mode === option.value }">
                            <span class="availability-radio-dot"></span>
                        </span>
                        <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                <span class="availability-card-title ppv2-text-black">{{ option.label }}</span>
                            </div>
                            <span class="availability-card-subtitle">{{ option.description }}</span>
                        </div>
                    </div>

                    <!-- ── Créneaux fixes ─────────────────────────────────────────── -->
                    <div v-if="option.value === 'fixed' && mode === 'fixed'" class="availability-card-body">
                        <div class="availability-row">
                            <span class="availability-row-label">{{ $t('experience.stepAvailability.validityDuration') }}</span>
                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                <select v-model="fixedDurationChoice" class="availability-select" @change="onFixedDurationChoice">
                                    <option v-for="d in durationOptions" :key="d.value" :value="d.value">{{ d.label }}</option>
                                </select>
                                <div v-if="fixedDurationChoice === 'custom'" class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                    <input
                                        type="number"
                                        :min="MIN_DURATION"
                                        :max="MAX_DURATION"
                                        step="5"
                                        class="availability-number"
                                        :value="fixedDuration"
                                        @change="onFixedDurationMinutes"
                                    />
                                    <span class="availability-hint">{{ $t('experience.stepAvailability.minutes') }}</span>
                                </div>
                            </div>
                        </div>
                        <p class="availability-hint ppv2-mt-2">{{ $t('experience.stepAvailability.fixedHelp') }}</p>
                        <p v-if="errors.fixed_duration" class="availability-error">{{ errors.fixed_duration }}</p>
                    </div>

                    <!-- ── Créneaux personnalisés ─────────────────────────────────── -->
                    <div v-if="option.value === 'custom' && mode === 'custom'" class="availability-card-body">
                        <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(8, true) }">
                            <div v-for="slot in customSlots" :key="slot.uid" class="availability-slot">
                                <!-- Ligne repliée -->
                                <div v-if="editingUid !== slot.uid" class="availability-slot-row" @click="openEdit(slot.uid)">
                                    <span class="availability-slot-name">{{ slot.name }}</span>
                                    <span class="availability-slot-range">
                                        {{ formatMinutes(slot.start) }} → {{ formatMinutes(slot.end) }}
                                    </span>
                                    <span v-if="slotError(slot)" class="availability-slot-flag">•</span>
                                    <button
                                        type="button"
                                        class="availability-icon-btn"
                                        :title="$t('experience.stepAvailability.edit')"
                                        @click.stop="openEdit(slot.uid)"
                                    >
                                        <Pencil class="ppv2-w-4 ppv2-h-4" />
                                    </button>
                                </div>

                                <!-- Formulaire d'édition -->
                                <div v-else class="availability-slot-form">
                                    <!-- Confirmation de suppression : remplace le formulaire -->
                                    <div v-if="confirmingDelete" class="ppv2-flex ppv2-flex-col ppv2-gap-3">
                                        <p class="availability-confirm-text">
                                            {{ $t('experience.stepAvailability.deleteConfirm', { name: slot.name }) }}
                                        </p>
                                        <div class="ppv2-flex ppv2-justify-end ppv2-gap-2">
                                            <button
                                                type="button"
                                                class="availability-btn-ghost"
                                                @click="confirmingDelete = false"
                                            >
                                                {{ $t('common.cancel') }}
                                            </button>
                                            <button type="button" class="availability-btn-danger" @click="deleteSlot(slot.uid)">
                                                {{ $t('experience.stepAvailability.delete') }}
                                            </button>
                                        </div>
                                    </div>

                                    <template v-else>
                                        <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
                                            <label class="availability-field-label">
                                                {{ $t('experience.stepAvailability.name') }}
                                            </label>
                                            <input
                                                v-model="slot.name"
                                                type="text"
                                                maxlength="40"
                                                class="availability-input"
                                                :class="{ 'availability-input--error': nameError(slot) }"
                                            />
                                            <p v-if="nameError(slot)" class="availability-error">{{ nameError(slot) }}</p>
                                        </div>

                                        <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
                                            <label class="availability-field-label">
                                                {{ $t('experience.stepAvailability.bookingRange') }}
                                            </label>
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                                                <!--
                                                    <select> natif : on doit désactiver individuellement les
                                                    horaires déjà couverts par un autre créneau et étiqueter
                                                    les heures du lendemain (J+1). Aucun composant partagé ne
                                                    le permet aujourd'hui.
                                                -->
                                                <select
                                                    v-model.number="slot.start"
                                                    class="availability-select"
                                                    :class="{ 'availability-select--error': rangeError(slot) }"
                                                    @focus="rememberStart(slot)"
                                                    @change="onStartChange(slot)"
                                                >
                                                    <option
                                                        v-for="opt in timeOptions(slot, 'start')"
                                                        :key="opt.value"
                                                        :value="opt.value"
                                                        :disabled="opt.disabled"
                                                    >
                                                        {{ opt.label }}
                                                    </option>
                                                </select>
                                                <span class="availability-arrow">→</span>
                                                <select
                                                    v-model.number="slot.end"
                                                    class="availability-select"
                                                    :class="{ 'availability-select--error': rangeError(slot) }"
                                                >
                                                    <option
                                                        v-for="opt in timeOptions(slot, 'end')"
                                                        :key="opt.value"
                                                        :value="opt.value"
                                                        :disabled="opt.disabled"
                                                    >
                                                        {{ opt.label }}
                                                    </option>
                                                </select>
                                            </div>
                                            <p v-if="rangeError(slot)" class="availability-error">{{ rangeError(slot) }}</p>
                                        </div>

                                        <div class="ppv2-flex ppv2-flex-col ppv2-gap-1">
                                            <label class="availability-field-label">
                                                {{ $t('experience.stepAvailability.validityDuration') }}
                                            </label>
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-flex-wrap">
                                                <select
                                                    :value="durationChoiceOf(slot)"
                                                    class="availability-select"
                                                    @change="onSlotDurationChoice(slot, $event)"
                                                >
                                                    <option v-for="d in durationOptions" :key="d.value" :value="d.value">
                                                        {{ d.label }}
                                                    </option>
                                                </select>
                                                <div v-if="isCustomDuration(slot)" class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                    <input
                                                        type="number"
                                                        :min="MIN_DURATION"
                                                        :max="MAX_DURATION"
                                                        step="5"
                                                        class="availability-number"
                                                        :value="slot.duration"
                                                        @change="onSlotDurationMinutes(slot, $event)"
                                                    />
                                                    <span class="availability-hint">
                                                        {{ $t('experience.stepAvailability.minutes') }}
                                                    </span>
                                                </div>
                                                <span class="availability-hint">{{ countLabel(slot) }}</span>
                                            </div>
                                        </div>

                                        <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-pt-1">
                                            <button
                                                type="button"
                                                class="availability-btn-link-danger"
                                                @click="confirmingDelete = true"
                                            >
                                                {{ $t('experience.stepAvailability.delete') }}
                                            </button>
                                            <div class="ppv2-flex ppv2-gap-2">
                                                <button type="button" class="availability-btn-ghost" @click="cancelEdit">
                                                    {{ $t('common.cancel') }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="availability-btn-primary"
                                                    :disabled="!!slotError(slot)"
                                                    @click="saveEdit(slot)"
                                                >
                                                    {{ $t('common.save') }}
                                                </button>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <button type="button" class="availability-add" @click="addSlot">
                                <Plus class="ppv2-w-4 ppv2-h-4" />
                                {{ $t('experience.stepAvailability.addSlot') }}
                            </button>

                            <p v-if="errors.custom_slots" class="availability-error">{{ errors.custom_slots }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!--
                Erreurs qui n'ont pas de place dans la carte active : un créneau personnalisé invalide
                bloque aussi l'enregistrement en mode journée/fixe (il serait persisté et deviendrait
                actif au moindre changement de mode), alors que son formulaire n'est rendu que dans la
                carte « Créneaux personnalisés ».
            -->
            <p v-if="errors.custom_slots && mode !== 'custom'" class="availability-error ppv2-mt-3">
                {{ $t('experience.stepAvailability.errors.invalidCustomSlotElsewhere', { error: errors.custom_slots }) }}
            </p>
            <p v-if="errors.availability_mode" class="availability-error ppv2-mt-3">{{ errors.availability_mode }}</p>

            <!--
                Décision produit (2026-09-30) : enregistrer des disponibilités retire, côté serveur,
                les tarifs horaires de l'étape « Options de billetterie » devenus non conformes au
                mode (journée entière en all_day, fenêtres exactes en custom). On le dit avant.
                Le compte est indicatif (même règle que TicketTarifCoherence) ; le serveur décide.
            -->
            <CustomDialog
                v-model="showPurgeConfirm"
                :defaultTitle="$t('experience.stepAvailability.purgeConfirmTitle')"
                customClassTitle="poppinsSemiBold"
                noScroll
            >
                <template #content>
                    <p class="ppv2-text-center ppv2-text-black poppinsRegular ppv2-text-[15px]">
                        {{ $t('experience.stepAvailability.purgeConfirm', { count: nonConformingTarifCount }) }}
                    </p>
                </template>
                <template #actions>
                    <BaseButton
                        type="button"
                        variant="outline"
                        customHeight="43"
                        customClass="!ppv2-min-h-[40px]"
                        @click="cancelPurge"
                    >
                        {{ $t('common.cancel') }}
                    </BaseButton>
                    <BaseButton
                        type="button"
                        variant="primary"
                        customHeight="43"
                        customClass="!ppv2-min-h-[40px]"
                        @click="confirmPurge"
                    >
                        {{ $t('common.confirm') }}
                    </BaseButton>
                </template>
            </CustomDialog>

            <!-- FOOTER ACTIONS -->
            <div
                class="form-actions !ppv2mb-0 !ppv2-static !ppv2-mt-auto ppv2-pt-6 ppv2-bg-white dark:ppv2-bg-dark ppv2-z-10"
                :class="{ '!ppv2-justify-end': complited }"
            >
                <BackButton v-show="!complited" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="submit" variant="primary" :loading="loading" @click="submitForm">
                    {{ !complited ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>

        <div class="lg:ppv2-pt-[70px] ppv2-pt-[35px]">
            <PreviewExperience v-if="!hidePreview" :experience="prestation" :step="step" />
        </div>
    </div>
</template>

<script>
import HeaderStep from './HeaderStep.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import PreviewExperience from './PreviewExperience.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import { formatDuration, formatMinutesOfDay } from '../../../../utils/duration'
import { Pencil, Plus } from 'lucide-vue'

/** Pas des sélecteurs d'heure, en minutes. */
const TIME_STEP = 15
/** Bornes de durée de validité (alignées sur PrestationAvailabilitySlot côté serveur). */
const MIN_DURATION = 5
const MAX_DURATION = 720
/** 03:00 du lendemain : fin maximale d'une plage (fermetures tardives). */
const MAX_END = 1620
// Largeur maximale d'une plage : le début est toujours du jour même (seule la fin porte le drapeau
// lendemain), une plage de plus de 24 h est donc un début après minuit envoyé sans drapeau. Même
// règle que le serveur (`range_max`).
const MAX_SPAN = 1440
/** Dernier début proposé : 23:45. */
const MAX_START = 1440 - TIME_STEP
/**
 * Fin de la PREMIÈRE proposition faite pour un nouveau créneau : le dernier pas de la grille avant
 * minuit. Une valeur par défaut ne doit pas partir d'elle-même sur une fermeture tardive.
 *
 * Ne borne QUE cette première proposition : dès que le créneau doit être décalé derrière un
 * créneau existant, c'est MAX_END (03:00 du lendemain) qui s'applique, sans quoi la fenêtre
 * d'après minuit deviendrait impossible à créer. Voir addSlot().
 *
 * Le ticket écrit « plafonnée à 23:59 », mais 23:59 n'est pas sur la grille de 15 min et
 * n'existe donc pas dans les listes d'heures.
 */
const MAX_NEW_SLOT_END = MAX_START
/** Durées proposées dans la liste, avant l'option « Personnalisé ». */
const PRESET_DURATIONS = [30, 60, 120, 180, 240]

let uidSeq = 0

/**
 * Billetterie — étape « Disponibilités ».
 *
 * Trois modes exclusifs. La configuration des modes non sélectionnés est CONSERVÉE (en mémoire ici,
 * et en base côté serveur) : changer de mode ne doit jamais détruire ce qui a été saisi ailleurs.
 *
 * Les heures sont manipulées en MINUTES DEPUIS MINUIT et non en "HH:MM", pour que les plages
 * franchissant minuit (jusqu'à 3h du lendemain = 1620) restent ordonnées et comparables. La
 * conversion en "HH:MM" + drapeau `next_day` n'a lieu qu'au moment d'émettre le payload.
 *
 * Aucune règle métier n'est décidée ici : tout est revalidé côté serveur par
 * Step4TicketAvailabilityRequest. Les contrôles présents ne servent qu'au confort de saisie.
 */
export default {
    name: 'StepTicketAvailability',
    components: { HeaderStep, BackButton, BaseButton, PreviewExperience, CustomDialog, Pencil, Plus },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        prestation: { type: Object, required: false },
        id: { type: [Number, String], default: null },
        complited: { type: Boolean, required: false },
        hidePreview: { type: Boolean, default: false },
    },
    data() {
        return {
            MIN_DURATION,
            MAX_DURATION,
            loading: false,
            // null tant que le billet n'a pas de mode enregistré : un billet existant
            // (availability_mode NULL) fonctionne sur le découpage historique, qui n'est aucun
            // des trois modes. Le présélectionner en « Toute la journée » transformait un simple
            // « Enregistrer » en changement de comportement. Le choix doit être explicite.
            mode: null,
            fixedDuration: 60,
            fixedDurationChoice: '60',
            customSlots: this.buildDefaultSlots(),
            editingUid: null,
            draftSnapshot: null,
            previousStart: null,
            loadedPrestationId: null,
            confirmingDelete: false,
            errors: {},
            initialDataString: null,
            // Confirmation avant purge des tarifs horaires non conformes (cf. saveExperience).
            showPurgeConfirm: false,
            purgeConfirmed: false,
            pendingSave: null,
        }
    },
    computed: {
        // Tarifs horaires (étape « Options de billetterie ») de toutes les variantes, tels que
        // chargés par le parent (tickets_as_option[].item_prestation.prestation_planning[].planning_prices[]).
        hourlyTarifs() {
            const options = this.prestation?.tickets_as_option || this.prestation?.ticketsAsOption || []
            return options.flatMap((option) => {
                const item = option.item_prestation || option.itemPrestation || {}
                const plannings = item.prestation_planning || item.prestationPlanning || []
                return (Array.isArray(plannings) ? plannings : []).flatMap((p) => p.planning_prices || p.planningPrices || [])
            })
        },
        // Nombre de tarifs horaires que le serveur retirera pour la configuration en cours de saisie
        // (miroir d'affichage de TicketTarifCoherence::conforms — le serveur reste seul juge).
        nonConformingTarifCount() {
            const hi = (t) => (t || '').substring(0, 5)
            if (this.mode === 'all_day') {
                return this.hourlyTarifs.filter((p) => !(hi(p.start_time) === '00:00' && hi(p.end_time) === '23:59')).length
            }
            if (this.mode === 'custom') {
                const windows = this.customSlots.map((s) => ({
                    start: this.toApiTime(s.start),
                    end: s.end >= 1440 ? '23:59' : this.toApiTime(s.end),
                }))
                if (!windows.length) return 0
                return this.hourlyTarifs.filter(
                    (p) => !windows.some((w) => w.start === hi(p.start_time) && w.end === hi(p.end_time))
                ).length
            }
            return 0
        },
        modeOptions() {
            return [
                {
                    value: 'all_day',
                    label: this.$t('experience.stepAvailability.allDay.title'),
                    description: this.$t('experience.stepAvailability.allDay.description'),
                },
                {
                    value: 'fixed',
                    label: this.$t('experience.stepAvailability.fixed.title'),
                    description: this.$t('experience.stepAvailability.fixed.description'),
                },
                {
                    value: 'custom',
                    label: this.$t('experience.stepAvailability.custom.title'),
                    description: this.$t('experience.stepAvailability.custom.description'),
                },
            ]
        },
        durationOptions() {
            const options = PRESET_DURATIONS.map((value) => ({
                value: String(value),
                label: formatDuration(value, this.$i18n),
            }))
            options.push({ value: 'custom', label: this.$t('experience.stepAvailability.customDuration') })
            return options
        },
    },
    methods: {
        normalizeSize,

        /** Créneaux proposés par défaut à la création d'un billet. */
        buildDefaultSlots() {
            return [
                { uid: ++uidSeq, id: null, name: 'Matin', start: 540, end: 720, duration: 60, isNew: false },
                { uid: ++uidSeq, id: null, name: 'Après-midi', start: 840, end: 1080, duration: 60, isNew: false },
                { uid: ++uidSeq, id: null, name: 'Soir', start: 1140, end: 1320, duration: 60, isNew: false },
            ]
        },

        /** Minutes depuis minuit → "HH:MM", suffixé (J+1) au-delà de minuit. */
        formatMinutes(minutes) {
            return formatMinutesOfDay(minutes, this.$t('experience.stepAvailability.nextDay'))
        },

        selectMode(value) {
            // On ne touche NI à fixedDuration NI à customSlots : les autres modes gardent leur
            // configuration (critère d'acceptation).
            this.mode = value
            this.$delete(this.errors, 'availability_mode')
            this.$delete(this.errors, 'custom_slots')
            this.$delete(this.errors, 'fixed_duration')
        },

        // ── Durées ────────────────────────────────────────────────────────────
        clampDuration(value) {
            const minutes = Math.round((Number(value) || 0) / 5) * 5
            return Math.max(MIN_DURATION, Math.min(MAX_DURATION, minutes))
        },
        onFixedDurationChoice() {
            if (this.fixedDurationChoice !== 'custom') this.fixedDuration = Number(this.fixedDurationChoice)
        },
        /**
         * `clampDuration` peut retomber sur la valeur déjà en mémoire (47 → 45 quand elle vaut
         * déjà 45, 1000 → 720 quand elle vaut déjà 720). Vue ne voyant alors aucun changement, il
         * ne re-rend pas, et le champ — lié en `:value`, donc à sens unique — continuerait
         * d'afficher la saisie rejetée pendant qu'une autre valeur est enregistrée. On réécrit
         * donc la valeur normalisée dans le DOM.
         */
        onFixedDurationMinutes(event) {
            const value = this.clampDuration(event.target.value)
            this.fixedDuration = value
            event.target.value = value
        },
        isCustomDuration(slot) {
            return !PRESET_DURATIONS.includes(Number(slot.duration))
        },
        durationChoiceOf(slot) {
            return this.isCustomDuration(slot) ? 'custom' : String(slot.duration)
        },
        onSlotDurationChoice(slot, event) {
            // Modifier la durée ne modifie JAMAIS la plage de réservation.
            const value = event.target.value
            this.$set(slot, 'duration', value === 'custom' ? 45 : Number(value))
        },
        /** Même raison que onFixedDurationMinutes : l'écran doit montrer ce qui sera enregistré. */
        onSlotDurationMinutes(slot, event) {
            const value = this.clampDuration(event.target.value)
            this.$set(slot, 'duration', value)
            event.target.value = value
        },

        // ── Sélecteurs d'heure ────────────────────────────────────────────────
        /** Plages occupées par les AUTRES créneaux. */
        busyRanges(slot) {
            return this.customSlots.filter((s) => s.uid !== slot.uid).map((s) => ({ start: s.start, end: s.end }))
        },
        /**
         * Options d'heure au pas de 15 min. Les horaires déjà couverts par un autre créneau sont
         * désactivés (« les durées déjà sélectionnées sont grisées ») plutôt que masqués, pour que
         * l'utilisateur comprenne pourquoi il ne peut pas les choisir.
         */
        timeOptions(slot, which) {
            const busy = this.busyRanges(slot)
            const taken = (minutes) => busy.some((b) => minutes >= b.start && minutes < b.end)
            // Une fin qui ENJAMBE un autre créneau (un créneau commence entre le début et cette fin)
            // produirait un chevauchement : on la grise, comme les heures déjà occupées.
            const spans = (minutes) => busy.some((b) => b.start >= slot.start && b.start < minutes)
            const from = which === 'start' ? 0 : slot.start + TIME_STEP
            const to = which === 'start' ? MAX_START : Math.min(MAX_END, slot.start + MAX_SPAN)
            const options = []

            for (let minutes = from; minutes <= to; minutes += TIME_STEP) {
                options.push({
                    value: minutes,
                    label: this.formatMinutes(minutes),
                    // Une fin peut tomber sur la borne de début d'un autre créneau (créneaux
                    // contigus), d'où le décalage d'un pas pour le champ « fin ».
                    disabled: which === 'start' ? taken(minutes) : taken(minutes - TIME_STEP) || spans(minutes),
                })
            }

            const current = which === 'start' ? slot.start : slot.end
            if (!options.some((o) => o.value === current)) {
                options.push({ value: current, label: this.formatMinutes(current), disabled: false })
                options.sort((a, b) => a.value - b.value)
            }

            return options
        },
        /**
         * Déplacer le début décale la fin pour conserver la LARGEUR de la plage.
         *
         * `v-model.number` écrit déjà `slot.start` avant que `@change` ne se déclenche : calculer
         * l'écart ici sur `slot.end - slot.start` le mesurait donc sur la nouvelle borne et réduisait
         * la plage à un seul pas (09:00–12:00 déplacé à 13:00 devenait 13:00–13:15). On mémorise la
         * borne précédente au `@focus` pour retrouver la vraie largeur.
         */
        rememberStart(slot) {
            this.previousStart = slot.start
        },
        onStartChange(slot) {
            const previous = this.previousStart
            const span = previous !== null && slot.end > previous ? slot.end - previous : 60

            this.$set(slot, 'end', Math.min(slot.start + span, MAX_END, slot.start + MAX_SPAN))
            this.previousStart = slot.start
        },

        // ── Validation d'un créneau ───────────────────────────────────────────
        nameError(slot) {
            const name = (slot.name || '').trim()
            if (!name) return this.$t('experience.stepAvailability.errors.nameRequired')
            if (name.length > 40) return this.$t('experience.stepAvailability.errors.nameTooLong')
            return ''
        },
        rangeError(slot) {
            if (slot.end <= slot.start) return this.$t('experience.stepAvailability.errors.endAfterStart')
            if (slot.end > MAX_END) return this.$t('experience.stepAvailability.errors.endMax')
            if (slot.end - slot.start > MAX_SPAN) return this.$t('experience.stepAvailability.errors.rangeMax')
            const clash = this.customSlots.find(
                (other) => other.uid !== slot.uid && slot.start < other.end && other.start < slot.end
            )
            if (clash) return this.$t('experience.stepAvailability.errors.overlap', { name: clash.name })
            return ''
        },
        slotError(slot) {
            return (
                this.nameError(slot) ||
                this.rangeError(slot) ||
                (this.subSlotCount(slot) < 1 ? this.$t('experience.stepAvailability.errors.rangeTooShort') : '')
            )
        },
        subSlotCount(slot) {
            const duration = Number(slot.duration) || 0
            if (duration < MIN_DURATION) return 0
            return Math.floor((slot.end - slot.start) / duration)
        },
        /** « soit 3 créneaux d'1 heure » — ou « Plage trop courte ». */
        countLabel(slot) {
            const count = this.subSlotCount(slot)
            if (count < 1) return this.$t('experience.stepAvailability.errors.rangeTooShort')
            return this.$tc('experience.stepAvailability.generatedCount', count, {
                count,
                duration: formatDuration(Number(slot.duration), this.$i18n),
            })
        },

        // ── Édition ───────────────────────────────────────────────────────────
        /**
         * Ouvrir un créneau referme le précédent EN CONSERVANT ses modifications : on abandonne
         * simplement sa copie de sauvegarde, on ne la restaure pas.
         */
        openEdit(uid) {
            const slot = this.customSlots.find((s) => s.uid === uid)
            if (!slot) return
            this.confirmingDelete = false
            this.editingUid = uid
            this.draftSnapshot = { ...slot }
        },
        /** Annuler restaure l'état initial ; un créneau jamais enregistré est supprimé. */
        cancelEdit() {
            const snapshot = this.draftSnapshot
            if (snapshot) {
                if (snapshot.isNew) {
                    this.customSlots = this.customSlots.filter((s) => s.uid !== snapshot.uid)
                } else {
                    const index = this.customSlots.findIndex((s) => s.uid === snapshot.uid)
                    if (index >= 0) this.$set(this.customSlots, index, { ...snapshot })
                }
            }
            this.editingUid = null
            this.draftSnapshot = null
            this.confirmingDelete = false
            this.$delete(this.errors, 'custom_slots')
        },
        saveEdit(slot) {
            if (this.slotError(slot)) return
            this.$set(slot, 'isNew', false)
            this.editingUid = null
            this.draftSnapshot = null
            this.confirmingDelete = false
        },
        deleteSlot(uid) {
            this.customSlots = this.customSlots.filter((s) => s.uid !== uid)
            this.editingUid = null
            this.draftSnapshot = null
            this.confirmingDelete = false
            if (!this.customSlots.length) {
                this.$set(this.errors, 'custom_slots', this.$t('experience.stepAvailability.errors.atLeastOneSlot'))
            }
        },
        /**
         * Nouveau créneau : fin du dernier + 1 h (début plafonné à 22:00), fin = début + 2 h
         * (plafonnée à 23:59). Le résultat est ensuite décalé sur le premier espace réellement
         * libre, sans quoi la valeur par défaut chevaucherait un créneau existant.
         */
        addSlot() {
            const last = this.customSlots[this.customSlots.length - 1]
            let start = last ? Math.min(last.end + 60, 22 * 60) : 9 * 60
            let end = Math.min(start + 120, MAX_NEW_SLOT_END)

            const busy = this.customSlots.map((s) => ({ start: s.start, end: s.end })).sort((a, b) => a.start - b.start)
            const overlaps = (from, to) => busy.some((b) => from < b.end && b.start < to)

            // Plafond DIFFÉRENT de celui de la valeur initiale, et c'est voulu : une fois décalé
            // derrière un créneau existant, le seul espace libre peut se trouver après minuit. Le
            // borner à 23:45 ici rendrait la fenêtre 01:00–03:00 impossible à créer — la fonction
            // rendrait « noFreeSpace » — alors que le ticket autorise explicitement les plages
            // jusqu'à 3 h du lendemain. Ne pas « harmoniser » les deux plafonds.
            while (overlaps(start, end) && start + TIME_STEP <= MAX_START) {
                const blocking = busy.find((b) => start < b.end && b.start < end)
                start = blocking.end
                end = Math.min(start + 120, MAX_END)
            }

            // Le début est toujours du jour même (seule la fin porte le drapeau lendemain) : un
            // décalage derrière un créneau qui finit après minuit ne doit pas produire un début
            // ≥ 24:00, que l'API relirait comme une heure du matin du jour même.
            if (overlaps(start, end) || end <= start || start > MAX_START) {
                this.$set(this.errors, 'custom_slots', this.$t('experience.stepAvailability.errors.noFreeSpace'))
                return
            }

            const slot = {
                uid: ++uidSeq,
                id: null,
                name: this.$t('experience.stepAvailability.newSlotName'),
                start,
                end,
                duration: 60,
                isNew: true,
            }

            this.customSlots.push(slot)
            this.$delete(this.errors, 'custom_slots')
            this.openEdit(slot.uid)
        },

        // ── Enregistrement de l'étape ─────────────────────────────────────────
        validateForm() {
            this.errors = {}

            if (!this.mode) {
                this.$set(this.errors, 'availability_mode', this.$t('experience.stepAvailability.errors.modeRequired'))
                return false
            }

            if (this.mode === 'fixed') {
                // Sur la valeur saisie, pas sur sa version bornée : clampDuration() la ramène
                // toujours dans l'intervalle, le contrôle ne pouvait donc jamais échouer.
                // « Durée > plage d'ouverture » est vérifié par le serveur, seul à connaître les horaires.
                const duration = Number(this.fixedDuration)
                if (!Number.isFinite(duration) || duration < MIN_DURATION || duration > MAX_DURATION || duration % 5 !== 0) {
                    this.$set(this.errors, 'fixed_duration', this.$t('experience.stepAvailability.errors.durationRange'))
                }
            }

            if (this.mode === 'custom' && !this.customSlots.length) {
                this.$set(this.errors, 'custom_slots', this.$t('experience.stepAvailability.errors.atLeastOneSlot'))
            }

            // Les créneaux personnalisés ne sont transmis (et persistés) qu'en mode « Créneaux
            // personnalisés » : c'est le seul mode où un créneau invalide bloque l'étape.
            const invalid = this.mode === 'custom' ? this.customSlots.find((slot) => this.slotError(slot)) : null
            if (invalid) {
                this.$set(this.errors, 'custom_slots', this.slotError(invalid))
                this.openEdit(invalid.uid)
            }

            return Object.keys(this.errors).length === 0
        },
        buildPayload() {
            const payload = {
                step: 4,
                is_ticket_availability: true,
                availability_mode: this.mode,
            }
            // La configuration d'un mode n'est transmise que lorsqu'il est actif. Côté serveur,
            // une clé absente signifie « ne pas toucher » : les créneaux personnalisés enregistrés
            // et la durée fixe survivent à un enregistrement dans un autre mode, et les 3 créneaux
            // d'exemple ne sont jamais persistés tant que le gestionnaire ne les a pas choisis.
            if (this.mode === 'fixed') {
                payload.fixed_duration = this.clampDuration(this.fixedDuration)
            }
            if (this.mode === 'custom') {
                payload.custom_slots = this.customSlots.map((slot) => ({
                    id: slot.id || null,
                    name: (slot.name || '').trim(),
                    // Le serveur attend "HH:MM" + un drapeau lendemain ; il refait la conversion
                    // en minutes de son côté.
                    start: this.toApiTime(slot.start),
                    end: this.toApiTime(slot.end),
                    next_day: slot.end >= 1440,
                    duration: this.clampDuration(slot.duration),
                }))
            }
            if (this.prestation?.id || this.id) payload.id = this.prestation?.id || this.id
            return payload
        },
        toApiTime(minutes) {
            return String(Math.floor(minutes / 60) % 24).padStart(2, '0') + ':' + String(minutes % 60).padStart(2, '0')
        },
        async submitForm() {
            if (!this.validateForm()) return
            const success = await this.saveExperience(false, true)
            if (success) {
                this.$toast.success(this.$t('experience.saved'))
                if (!this.complited) this.$emit('next-step')
            }
        },
        /**
         * Appelé par create.vue via $refs pour « Enregistrer et quitter ».
         *
         * Si la configuration saisie rend des tarifs horaires non conformes, on demande confirmation
         * d'abord : l'enregistrement est mis en attente (pendingSave) et rejoué tel quel après accord.
         */
        async saveExperience(redirect = false, fromSubmit = false) {
            if (!this.validateForm()) return false
            if (!this.purgeConfirmed && this.nonConformingTarifCount > 0) {
                this.pendingSave = { redirect, fromSubmit }
                this.showPurgeConfirm = true
                return false
            }
            this.purgeConfirmed = false
            this.loading = true
            try {
                const response = await axios.post('/api/v3/experience/save_exit_step_two', this.buildPayload())
                this.$emit('update-prestation', response.data.data)
                this.loadFromPrestation(response.data.data)
                this.initialDataString = this.getFormDataString()
                const purged = Number(response.data?.purged_hourly_tarifs || 0)
                if (purged > 0) {
                    this.$toast.success(this.$t('experience.stepAvailability.purgedToast', { count: purged }))
                }
                if (redirect) {
                    window.location.href = `${window.location.protocol}//${window.location.host}/back-office/v3/experience`
                }
                return true
            } catch (err) {
                console.error('❌ StepTicketAvailability - Save error:', err.response || err)
                if (err.response?.status === 422 && err.response?.data?.errors) {
                    this.applyServerErrors(err.response.data.errors)
                }
                return false
            } finally {
                this.loading = false
            }
        },
        confirmPurge() {
            this.showPurgeConfirm = false
            this.purgeConfirmed = true
            const pending = this.pendingSave || { redirect: false, fromSubmit: false }
            this.pendingSave = null
            if (pending.fromSubmit) return this.submitForm()
            return this.saveExperience(!!pending.redirect)
        },
        cancelPurge() {
            this.showPurgeConfirm = false
            this.purgeConfirmed = false
            this.pendingSave = null
        },
        /**
         * Traduit le sac d'erreurs Laravel (valeurs en tableaux, clés `custom_slots.N.champ`) vers
         * les emplacements d'affichage du composant : `errors.<champ>` est une chaîne, et l'erreur
         * d'un créneau est rattachée à ce créneau, dont on ouvre le formulaire.
         */
        applyServerErrors(serverErrors) {
            const first = (value) => (Array.isArray(value) ? value[0] : value)
            const errors = {}
            let slotIndex = null

            Object.keys(serverErrors).forEach((key) => {
                const match = key.match(/^custom_slots\.(\d+)\./)
                if (match) {
                    if (slotIndex === null) {
                        slotIndex = Number(match[1])
                        const slot = this.customSlots[slotIndex]
                        errors.custom_slots = slot ? `${slot.name} : ${first(serverErrors[key])}` : first(serverErrors[key])
                    }
                    return
                }
                errors[key.split('.')[0]] = first(serverErrors[key])
            })

            this.errors = errors

            const slot = slotIndex !== null ? this.customSlots[slotIndex] : null
            if (slot && this.mode === 'custom') this.openEdit(slot.uid)
        },
        /** Rejoue la configuration enregistrée (édition, ou retour de sauvegarde). */
        loadFromPrestation(prestationData = null) {
            const data = prestationData || this.prestation
            if (!data) return

            if (data.availability_mode) this.mode = data.availability_mode

            if (data.availability_duration) {
                this.fixedDuration = Number(data.availability_duration)
                this.fixedDurationChoice = PRESET_DURATIONS.includes(this.fixedDuration) ? String(this.fixedDuration) : 'custom'
            }

            const slots = data.availability_slots || data.availabilitySlots
            if (Array.isArray(slots) && slots.length) {
                this.customSlots = slots.map((slot) => ({
                    uid: ++uidSeq,
                    id: slot.id,
                    name: slot.name,
                    start: Number(slot.start_minutes),
                    end: Number(slot.end_minutes),
                    duration: Number(slot.duration),
                    isNew: false,
                }))
            }

            this.editingUid = null
            this.draftSnapshot = null
        },

        // ── Garde « modifications non enregistrées » (create.vue) ─────────────
        getFormDataString() {
            return JSON.stringify({
                mode: this.mode,
                fixedDuration: this.fixedDuration,
                customSlots: this.customSlots.map((s) => ({
                    id: s.id,
                    name: s.name,
                    start: s.start,
                    end: s.end,
                    duration: s.duration,
                })),
            })
        },
        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },
    },
    watch: {
        prestation: {
            /**
             * create.vue partage un unique `localPrestation` et le réassigne à chaque rafraîchissement.
             * Recharger inconditionnellement écrasait les créneaux en cours d'édition : le formulaire
             * ouvert se refermait et la saisie disparaissait sans avertissement. On ne recharge donc
             * que lorsqu'on change réellement de billet, et jamais pendant une édition.
             */
            handler(value) {
                if (!value || !value.id) return
                if (this.editingUid !== null) return
                if (this.loadedPrestationId === value.id) return

                this.loadedPrestationId = value.id
                this.loadFromPrestation()
                this.$nextTick(() => {
                    this.initialDataString = this.getFormDataString()
                })
            },
            immediate: true,
            deep: true,
        },
    },
    mounted() {
        if (!this.initialDataString) this.initialDataString = this.getFormDataString()
    },
}
</script>

<style scoped>
/*
 * Vocabulaire visuel aligné sur le reste du wizard Expérience :
 *   - neutres : famille indigo de create.vue et StepTicketOptions.vue — rgba(84, 85, 149, …)
 *   - accent  : #7d2dbd, qui est déjà l'accent « étape active » du rail de gauche
 *   - typo    : l'échelle clamp() que produit utils/normalizeFontSize.js sur une base 1920px,
 *               écrite directement en CSS pour ne pas imposer un :style à chaque élément.
 *
 * Les custom properties sont portées par .availability-modes et NON par :root : en
 * <style scoped> Vue réécrit le sélecteur en :root[data-v-xxx], qui ne matche jamais rien.
 * Tous les éléments .availability-* descendent de ce conteneur, l'héritage suffit.
 */
.availability-modes {
    /* Neutres — famille indigo du wizard */
    --av-border: rgba(84, 85, 149, 0.12);
    --av-border-strong: rgba(84, 85, 149, 0.2);
    --av-hover: rgba(84, 85, 149, 0.08);
    --av-text: #151933;
    --av-text-muted: #545595;
    --av-text-faint: rgba(84, 85, 149, 0.55);
    --av-surface: #fff;

    /* Accent de sélection */
    --av-accent: #7d2dbd;
    --av-accent-soft: #f3eafa;
    --av-accent-surface: #faf7fe;
    --av-accent-deep: #571f84;
    --av-accent-dashed: #c398e6;
    --av-accent-muted: #b09cc4;
    --av-focus-ring: rgba(125, 45, 189, 0.15);

    --av-danger: #b3261e;
    --av-danger-deep: #8f1e17;

    /* Échelle typographique = normalizeFontSize(14 | 13 | 12 | 11) */
    --av-fs-14: clamp(12px, 0.7292vw, 16px);
    --av-fs-13: clamp(11px, 0.6771vw, 15px);
    --av-fs-12: clamp(11px, 0.625vw, 14px);
    --av-fs-11: clamp(11px, 0.5729vw, 13px);

    /* Métriques : le retrait du corps de carte est dérivé, il n'est plus codé en dur */
    --av-radio-size: 18px;
    --av-radio-gap: 12px;
    --av-card-pad: 16px;
    --av-content-max: 640px;
}

/* ── Carte de mode ──────────────────────────────────────────────────────── */
.availability-card {
    border: 1px solid var(--av-border);
    border-radius: 12px;
    background: var(--av-surface);
    overflow: hidden;
    max-width: var(--av-content-max);
    transition:
        border-color 0.15s ease,
        background 0.15s ease;
}
/* Les trois cartes sont l'élément interactif principal de l'étape : elles doivent réagir. */
.availability-card:not(.availability-card--selected):hover {
    border-color: var(--av-border-strong);
    background: var(--av-hover);
}
.availability-card--selected {
    border-color: var(--av-accent);
    background: var(--av-accent-surface);
}
.availability-card-head {
    display: flex;
    gap: var(--av-radio-gap);
    padding: 14px var(--av-card-pad);
    cursor: pointer;
}
.availability-radio {
    width: var(--av-radio-size);
    height: var(--av-radio-size);
    margin-top: 2px;
    flex: none;
    border-radius: 999px;
    border: 1.5px solid var(--av-border-strong);
    background: var(--av-surface);
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        border-color 0.15s ease,
        background 0.15s ease;
}
.availability-radio--on {
    border-color: var(--av-accent);
    background: var(--av-accent);
}
.availability-radio-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: transparent;
}
.availability-radio--on .availability-radio-dot {
    background: #fff;
}
.availability-card-title {
    font-size: var(--av-fs-14);
    font-weight: 500;
}
.availability-card-subtitle {
    font-size: var(--av-fs-12);
    color: var(--av-text-muted);
}
/* Le retrait à gauche aligne le corps sous le titre : padding + radio + gouttière. */
.availability-card-body {
    padding: 4px var(--av-card-pad) var(--av-card-pad) calc(var(--av-card-pad) + var(--av-radio-size) + var(--av-radio-gap));
}
.availability-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: var(--av-surface);
    border: 1px solid var(--av-border);
    border-radius: 8px;
    padding: 10px 12px;
}
.availability-row-label {
    font-size: var(--av-fs-13);
    color: var(--av-text);
}

/* ── Champs ─────────────────────────────────────────────────────────────── */
.availability-select,
.availability-input,
.availability-number {
    height: 36px;
    padding: 0 8px;
    border: 1px solid var(--av-border-strong);
    border-radius: 8px;
    font-size: var(--av-fs-13);
    /* background-color et non background : le raccourci écraserait le chevron du <select>. */
    background-color: var(--av-surface);
    color: var(--av-text);
    outline: none;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
}
/*
 * Le <select> natif est conservé volontairement (options désactivables + libellés « J+1 »,
 * cf. commentaire du template), mais on lui redessine le chevron pour qu'il ait la même
 * silhouette que les champs BaseSelect du reste du wizard, au lieu de la flèche de l'OS.
 */
.availability-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding-right: 28px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='none' stroke='%23545595' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='m3 4.5 3 3 3-3'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
}
/* `outline: none` ci-dessus n'est acceptable qu'accompagné de ce remplacement. */
.availability-select:focus-visible,
.availability-input:focus-visible,
.availability-number:focus-visible {
    border-color: var(--av-accent);
    box-shadow: 0 0 0 3px var(--av-focus-ring);
}
.availability-number {
    width: 84px;
}
.availability-input {
    width: 100%;
}
.availability-select--error,
.availability-input--error {
    border-color: var(--av-danger);
}
.availability-hint {
    font-size: var(--av-fs-12);
    color: var(--av-text-muted);
}
.availability-error {
    font-size: var(--av-fs-12);
    line-height: 1.45;
    color: var(--av-danger);
    margin: 2px 0 0;
}
/* ── Créneaux personnalisés ─────────────────────────────────────────────── */
/*
 * Deux niveaux de surface, au lieu de trois. Le conteneur .availability-slot ne dessine
 * plus rien : ce sont la ligne repliée et le formulaire d'édition qui portent bordure et
 * fond. Cela supprime le double encadré (carte teintée → créneau blanc → formulaire blanc
 * rebordé) sans rien changer au DOM.
 */
.availability-slot {
    border-radius: 8px;
}
.availability-slot-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 10px 11px 14px;
    cursor: pointer;
    border: 1px solid var(--av-border);
    border-radius: 8px;
    background: var(--av-surface);
    transition:
        border-color 0.15s ease,
        background 0.15s ease;
}
.availability-slot-row:hover {
    border-color: var(--av-border-strong);
    background: var(--av-hover);
}
.availability-slot-name {
    flex: 1;
    font-size: var(--av-fs-13);
    font-weight: 500;
    color: var(--av-text);
}
.availability-slot-range {
    font-size: var(--av-fs-13);
    color: var(--av-text-muted);
    font-variant-numeric: tabular-nums;
}
.availability-slot-flag {
    color: var(--av-danger);
    font-weight: 700;
}
.availability-slot-form {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border: 1px solid var(--av-accent);
    border-radius: 8px;
    background: var(--av-surface);
}
.availability-field-label {
    font-size: var(--av-fs-11);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--av-text-muted);
}
.availability-arrow {
    color: var(--av-text-faint);
}
.availability-icon-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: 6px;
    color: var(--av-text-muted);
    cursor: pointer;
    transition:
        background 0.15s ease,
        color 0.15s ease;
}
.availability-icon-btn:hover {
    background: var(--av-accent-soft);
    color: var(--av-accent);
}
.availability-add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 40px;
    border: 1px dashed var(--av-accent-dashed);
    border-radius: 8px;
    background: var(--av-surface);
    color: var(--av-accent);
    font-size: var(--av-fs-13);
    font-weight: 500;
    cursor: pointer;
    transition:
        background 0.15s ease,
        border-color 0.15s ease;
}
.availability-add:hover {
    background: var(--av-accent-soft);
    border-color: var(--av-accent);
}
/* ── Boutons ────────────────────────────────────────────────────────────── */
.availability-btn-ghost,
.availability-btn-primary,
.availability-btn-danger {
    padding: 7px 14px;
    border-radius: 8px;
    font-size: var(--av-fs-13);
    font-weight: 500;
    cursor: pointer;
    transition:
        background 0.15s ease,
        border-color 0.15s ease;
}
.availability-btn-ghost {
    border: 1px solid var(--av-border-strong);
    background: var(--av-surface);
    color: var(--av-text-muted);
}
.availability-btn-ghost:hover {
    background: var(--av-hover);
}
.availability-btn-primary {
    border: none;
    background: var(--av-accent);
    color: #fff;
    font-weight: 600;
}
.availability-btn-primary:hover:not(:disabled) {
    background: var(--av-accent-deep);
}
.availability-btn-primary:disabled {
    background: var(--av-accent-soft);
    color: var(--av-accent-muted);
    cursor: not-allowed;
}
.availability-btn-danger {
    border: none;
    background: var(--av-danger);
    color: #fff;
}
.availability-btn-danger:hover {
    background: var(--av-danger-deep);
}
.availability-btn-link-danger {
    border: none;
    background: transparent;
    padding: 0;
    font-size: var(--av-fs-13);
    font-weight: 500;
    color: var(--av-danger);
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: text-decoration-color 0.15s ease;
}
.availability-btn-link-danger:hover {
    text-decoration-color: currentColor;
}
.availability-confirm-text {
    font-size: var(--av-fs-13);
    color: var(--av-text);
    margin: 0;
}

/*
 * Anneau de focus clavier. Sans lui, la navigation au clavier est invisible sur cette étape.
 * Deux ombres (surface puis accent) pour rester lisible aussi bien sur le fond blanc d'une
 * carte repliée que sur le fond teinté d'une carte sélectionnée.
 */
.availability-btn-ghost:focus-visible,
.availability-btn-primary:focus-visible,
.availability-btn-danger:focus-visible,
.availability-icon-btn:focus-visible,
.availability-add:focus-visible {
    outline: none;
    box-shadow:
        0 0 0 2px var(--av-surface),
        0 0 0 4px var(--av-accent);
}
/* Bouton texte : un outline décalé rend mieux qu'une ombre collée aux glyphes. */
.availability-btn-link-danger:focus-visible {
    outline: 2px solid var(--av-accent);
    outline-offset: 3px;
    border-radius: 3px;
}
</style>
